// src/redux/citiesSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Configuration from "../configuration";

// Fetch all cities
export const fetchCities = createAsyncThunk("cities/fetchAll", async () => {
  const token = localStorage.getItem("x-access-token");
  const response = await fetch(Configuration.BACK_BASEURL + "cities", {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      /* "x-access-token": token, */
    },
  });
  const cities = await response.json();
  return cities;
});

// Fetch active cities only
export const fetchActiveCities = createAsyncThunk(
  "cities/fetchActive",
  async () => {
    const token = localStorage.getItem("x-access-token");
    const response = await fetch(
      Configuration.BACK_BASEURL + "cities/getIsActived",
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          /* "x-access-token": token, */
        },
      },
    );
    const cities = await response.json();
    return cities;
  },
);

// Fetch city by ID
export const cityGetById = createAsyncThunk("cities/getById", async (id) => {
  const token = localStorage.getItem("x-access-token");
  const response = await fetch(Configuration.BACK_BASEURL + `cities/${id}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      /* "x-access-token": token, */
    },
  });
  const city = await response.json();
  return city;
});

// Create new city
export const cityAdded = createAsyncThunk("cities/create", async (cityData) => {
  const token = localStorage.getItem("x-access-token");
  const response = await fetch(Configuration.BACK_BASEURL + "cities", {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      /* "x-access-token": token, */
    },
    body: JSON.stringify(cityData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to create city");
  }

  const city = await response.json();
  return city;
});

// Update city
export const cityUpdated = createAsyncThunk(
  "cities/update",
  async ({ id, ...cityData }) => {
    const token = localStorage.getItem("x-access-token");
    const response = await fetch(Configuration.BACK_BASEURL + `cities/${id}`, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        /* "x-access-token": token, */
      },
      body: JSON.stringify(cityData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to update city");
    }

    const city = await response.json();
    return city;
  },
);

// Delete city
export const cityDeleted = createAsyncThunk("cities/delete", async (id) => {
  const token = localStorage.getItem("x-access-token");
  const response = await fetch(Configuration.BACK_BASEURL + `cities/${id}`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      /* "x-access-token": token, */
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to delete city");
  }

  return id;
});

// Toggle city active status
export const toggleCityActive = createAsyncThunk(
  "cities/toggleActive",
  async ({ id, isActive }) => {
    const token = localStorage.getItem("x-access-token");
    const response = await fetch(
      Configuration.BACK_BASEURL + `cities/toggleActive/${id}`,
      {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          /* "x-access-token": token, */
        },
        body: JSON.stringify({ isActive }),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to toggle city status");
    }

    const city = await response.json();
    return city;
  },
);

const citiesSlice = createSlice({
  name: "cities",
  initialState: {
    entities: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all cities
      .addCase(fetchCities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
      })
      .addCase(fetchCities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Fetch active cities
      .addCase(fetchActiveCities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveCities.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
      })
      .addCase(fetchActiveCities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Add city
      .addCase(cityAdded.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cityAdded.fulfilled, (state, action) => {
        state.loading = false;
        state.entities.push(action.payload);
        state.error = null;
      })
      .addCase(cityAdded.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Update city
      .addCase(cityUpdated.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cityUpdated.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.entities.findIndex(
          (city) => city.id === action.payload.id,
        );
        if (index !== -1) {
          state.entities[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(cityUpdated.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Delete city
      .addCase(cityDeleted.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cityDeleted.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = state.entities.filter(
          (city) => city.id !== action.payload,
        );
        state.error = null;
      })
      .addCase(cityDeleted.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Toggle city active status
      .addCase(toggleCityActive.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleCityActive.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.entities.findIndex(
          (city) => city.id === action.payload.id,
        );
        if (index !== -1) {
          state.entities[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(toggleCityActive.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { clearError } = citiesSlice.actions;
export default citiesSlice.reducer;
